// A driver's agreements, on their driver page: which terms they are on today,
// every contract they were sent with its version, Addendum No. 1 and its
// history, the induction pack, and the stored documents. Read-only; the
// actions live on the Agreements page. db/migrations/3706, docs/AGREEMENTS.md.
import { useEffect } from 'react'
import { FileSignature, FileText } from 'lucide-react'
import { useGetDriverAgreements } from '../../hooks/backend/agreements'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { Chip } from '../interviews/Chip'
import { formatDay, formatWhen } from '../../utils/loans'
import { ADDENDUM_STATUS, DOCUMENT_SET_LABEL, MESSAGE_STATUS_LABEL, type DriverAgreements } from '../../utils/agreements'
import { useOpenAgreementFile } from '../../hooks/useOpenAgreementFile'

const EVENT_LABEL: Record<string, string> = {
  created: 'Offered',
  invitation_queued: 'Invitation emailed',
  attended_session: 'Present at the session',
  envelope_sent: 'Sent to sign',
  envelope_failed: 'Sending failed',
  signed: 'Signed',
  declined: 'Declined',
  reopened: 'Reopened',
}

// Only these events carry a note worth reading; on the others it repeats the label.
const NOTE_EVENTS = new Set(['declined', 'reopened', 'envelope_failed'])

export function DriverAgreementsPanel({ applicationId }: { applicationId: string }) {
  const q = useGetDriverAgreements<DriverAgreements | null>()
  const openFile = useOpenAgreementFile()
  const load = q.trigger
  useEffect(() => { void load({ application_id: applicationId }, { skipCache: true }).catch(() => undefined) }, [load, applicationId])
  const d = q.data

  return (
    <section className="space-y-2 rounded-lg border border-border bg-card p-3">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground"><FileSignature className="h-4 w-4" /> Agreements</h3>
      {q.loading && !d ? <p className="flex items-center gap-2 text-xs text-muted-foreground"><Spinner className="h-3 w-3" /> Loading…</p>
        : q.error ? <p className="text-xs text-rose-700 dark:text-rose-300">{q.error}</p>
        : !d ? <p className="text-xs text-muted-foreground">Not available to this login.</p>
        : (
          <div className="space-y-3 text-xs">
            <p className="text-foreground">
              {d.terms
                ? <>On <span className="font-medium">{d.terms.terms}</span>{d.terms.agreement_signed_on ? `, signed ${formatDay(d.terms.agreement_signed_on)}` : ''}.
                  {d.terms.addendum_terms_apply && d.terms.addendum_terms_from ? ` The v6 and Addendum No. 1 rules apply from ${formatDay(d.terms.addendum_terms_from)}.` : ''}</>
                : 'Not a driver yet, so no agreement is in force.'}
            </p>

            {d.addendum && (
              <div className="space-y-1 rounded-md border border-border p-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-foreground">Addendum No. 1</span>
                  <Chip tone={ADDENDUM_STATUS[d.addendum.status].tone}>{ADDENDUM_STATUS[d.addendum.status].label}</Chip>
                  {d.addendum.invitation_status && <span className="text-muted-foreground">Email {MESSAGE_STATUS_LABEL[d.addendum.invitation_status] ?? d.addendum.invitation_status}</span>}
                  {d.addendum.has_signed_pdf && <Button size="sm" variant="ghost" onClick={() => void openFile({ addendum_id: d.addendum!.id })}><FileText className="mr-1 h-3 w-3" /> Signed copy</Button>}
                </div>
                {d.addendum.decline_note && <p className="text-muted-foreground">{d.addendum.decline_note}</p>}
                <ul className="space-y-0.5 text-muted-foreground">
                  {d.addendum.events.map((e, i) => <li key={i}>{formatWhen(e.at)}: {EVENT_LABEL[e.event] ?? e.event}{e.note && NOTE_EVENTS.has(e.event) ? `. ${e.note}` : ''}</li>)}
                </ul>
              </div>
            )}

            {d.contracts.length > 0 && (
              <div>
                <p className="mb-1 font-medium text-foreground">Contracts sent</p>
                <ul className="space-y-0.5">
                  {d.contracts.map((c) => (
                    <li key={c.envelope_id} className="flex flex-wrap items-center gap-2">
                      <Chip tone={c.version === 'v6' ? 'brand' : 'neutral'}>{c.version ?? 'version unknown'}</Chip>
                      <span className="text-foreground">{c.status}</span>
                      <span className="text-muted-foreground">{c.signed_at ? `signed ${formatDay(c.signed_at)}` : c.sent_at ? `sent ${formatDay(c.sent_at)}` : ''}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {d.pack && (
              <p className="text-muted-foreground">
                Induction pack {d.pack.pack_version}: {d.pack.sent ? `emailed (${MESSAGE_STATUS_LABEL[d.pack.message_status ?? ''] ?? d.pack.message_status ?? 'queued'})` : d.pack.note ?? 'not emailed'}
              </p>
            )}

            {d.documents.length > 0 && (
              <div>
                <p className="mb-1 font-medium text-foreground">Documents</p>
                <ul className="space-y-0.5">
                  {d.documents.map((doc) => (
                    <li key={doc.id} className="flex flex-wrap items-center gap-2">
                      <span className="flex-1 text-foreground">{doc.title} <span className="text-muted-foreground">({DOCUMENT_SET_LABEL[doc.document_set] ?? doc.document_set})</span></span>
                      <Button size="sm" variant="ghost" onClick={() => void openFile({ document_id: doc.id })}><FileText className="mr-1 h-3 w-3" /> Open</Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
    </section>
  )
}
